import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Card,
  CardContent,
  TextField,
  IconButton,
  Chip
} from '@mui/material';
import axios from 'axios';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/dateUtils';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import { compressImageIfNeeded, getCompressionOptions } from '../utils/imageCompression';

const UpdateProofCreate = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [updateOrder, setUpdateOrder] = useState(null);
  const [relatedOrder, setRelatedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [proofCreated, setProofCreated] = useState(false);
  const [createdProofUrl, setCreatedProofUrl] = useState(null);
  const [createdProofSlug, setCreatedProofSlug] = useState(null);
  const [cancellingProof, setCancellingProof] = useState(false);

  // Per-image state: array of { file, preview, name, compressing, mimeType }
  const [artworkImages, setArtworkImages] = useState([]);
  const [proofNotes, setProofNotes] = useState('');

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const token = await getToken();

        const updateResponse = await axios.get(`${config.apiUrl}/api/orders/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (updateResponse.data?.success) {
          const updateData = updateResponse.data.data;
          setUpdateOrder(updateData);

          if (updateData.relatedOrderId) {
            const relatedResponse = await axios.get(
              `${config.apiUrl}/api/orders/${updateData.relatedOrderId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (relatedResponse.data?.success) {
              setRelatedOrder(relatedResponse.data.data);
            }
          }
        } else {
          setError('Failed to load update order details');
        }
      } catch (err) {
        setError(err.message || 'Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId, getToken]);

  const getUpdateTypes = useCallback(() => {
    if (!updateOrder) return [];
    const types = [];
    if (updateOrder.updateType === 'Header Update') types.push('header');
    if (updateOrder.orderUpdateType) {
      const t = updateOrder.orderUpdateType.toLowerCase();
      if (t.includes('artwork')) types.push('artwork');
      if (t.includes('dates')) types.push('dates');
      if (t.includes('link')) types.push('link');
      if (t.includes('header')) types.push('header');
    }
    return types;
  }, [updateOrder]);

  const getPrimaryUpdateType = useCallback(() => {
    const types = getUpdateTypes();
    if (types.includes('artwork')) return 'artwork';
    if (types.includes('header')) return 'header';
    if (types.includes('dates')) return 'dates';
    if (types.includes('link')) return 'link';
    return null;
  }, [getUpdateTypes]);

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    event.target.value = '';

    for (const file of files) {
      const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowed.includes(file.type)) {
        setError(`Invalid file type: "${file.name}". Use JPEG, PNG, GIF, or WebP.`);
        continue;
      }

      const tempId = `temp_${Date.now()}_${Math.random()}`;
      setArtworkImages(prev => [
        ...prev,
        { id: tempId, file: null, preview: null, name: file.name, compressing: true, mimeType: file.type }
      ]);

      try {
        const opts = getCompressionOptions('proof');
        const processedFile = await compressImageIfNeeded(file, opts);
        const preview = URL.createObjectURL(processedFile);
        setArtworkImages(prev =>
          prev.map(img =>
            img.id === tempId
              ? { id: tempId, file: processedFile, preview, name: processedFile.name || file.name, compressing: false, mimeType: processedFile.type || file.type }
              : img
          )
        );
      } catch (err) {
        setArtworkImages(prev => prev.filter(img => img.id !== tempId));
        setError(`Failed to process "${file.name}": ${err.message}`);
      }
    }
  };

  const handleRemoveImage = (id) => {
    setArtworkImages(prev => {
      const img = prev.find(i => i.id === id);
      if (img?.preview) URL.revokeObjectURL(img.preview);
      return prev.filter(i => i.id !== id);
    });
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const fullUrl = reader.result;
        const commaIdx = fullUrl.indexOf(',');
        const meta = fullUrl.substring(0, commaIdx);
        const base64Data = fullUrl.substring(commaIdx + 1);
        const mimeType = meta.split(':')[1]?.split(';')[0] || 'image/jpeg';
        resolve({ data: base64Data, mimeType });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleCreateProof = async () => {
    setUploading(true);
    setError(null);
    try {
      const token = await getToken();
      const updateType = getPrimaryUpdateType();
      const updateTypes = getUpdateTypes();
      const needsArtwork = updateTypes.includes('artwork') || updateTypes.includes('header');

      const proofData = {
        orderId: parseInt(orderId),
        updateType,
        notes: proofNotes,
        originalOrderId: relatedOrder?.id,
        updateOrderData: {
          orderUpdateType: updateOrder.orderUpdateType,
          totalPrice: updateOrder.totalPrice,
          newEndDate: updateOrder.newEndDate,
          eLinkType: updateOrder.eLinkType,
          eLinkContent: updateOrder.eLinkContent
        }
      };

      if (needsArtwork && artworkImages.length > 0) {
        const images = await Promise.all(
          artworkImages
            .filter(img => img.file)
            .map(async (img) => {
              const { data, mimeType } = await fileToBase64(img.file);
              return { data, mimeType, filename: img.name };
            })
        );
        proofData.images = images;
      }

      const response = await axios.post(
        `${config.apiUrl}/api/proof/update-order`,
        proofData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      if (response.data?.success) {
        setCreatedProofUrl(response.data.data?.proofUrl || null);
        setCreatedProofSlug(response.data.data?.proofSlug || null);
        setProofCreated(true);
      } else {
        throw new Error(response.data?.message || 'Failed to create proof');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create proof');
    } finally {
      setUploading(false);
    }
  };

  const handleCancelProofAfterCreation = async () => {
    setCancellingProof(true);
    try {
      const token = await getToken();
      await axios.post(
        `${config.apiUrl}/api/proof/cancel/${orderId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate('/app/workflow');
    } catch (err) {
      // Navigate anyway even if cancel fails
      navigate('/app/workflow');
    } finally {
      setCancellingProof(false);
    }
  };

  const handleCancelBeforeCreate = async () => {
    try {
      const token = await getToken();
      await axios.put(
        `${config.apiUrl}/api/orders/${orderId}/status`,
        { status: 'In Design' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      // ignore
    }
    navigate('/app/workflow');
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 2, mb: 2 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !updateOrder) {
    return (
      <Container maxWidth={false} sx={{ mt: 2, mb: 2 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/app/workflow')} sx={{ mt: 2 }}>
          Back to Orders
        </Button>
      </Container>
    );
  }

  // ── Success screen ────────────────────────────────────────────────────────
  if (proofCreated) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6, mb: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <CheckCircleIcon sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Proof Created Successfully
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            The proof has been generated and the order status updated to{' '}
            <strong>Proof Active</strong>.
          </Typography>

          {createdProofUrl && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1, wordBreak: 'break-all', textAlign: 'left' }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Proof URL
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>{createdProofUrl}</Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<OpenInNewIcon />}
                onClick={() => window.open(createdProofUrl, '_blank')}
                sx={{ mr: 1 }}
              >
                View Proof
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={cancellingProof ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
              onClick={handleCancelProofAfterCreation}
              disabled={cancellingProof}
            >
              {cancellingProof ? 'Cancelling...' : 'Cancel Proof'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/app/workflow')}
            >
              Back to Orders
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  const updateType = getPrimaryUpdateType();
  const updateTypes = getUpdateTypes();
  const hasArtwork = updateTypes.includes('artwork');
  const hasHeader = updateTypes.includes('header');
  const hasDates = updateTypes.includes('dates');
  const hasLink = updateTypes.includes('link');
  const needsArtwork = hasArtwork || hasHeader;
  const isCompressing = artworkImages.some(img => img.compressing);
  const canCreate = needsArtwork ? (artworkImages.filter(i => i.file).length > 0 && !isCompressing) : true;

  const businessName = updateOrder?.businessName || relatedOrder?.businessName || `Order #${orderId}`;

  return (
    <Container maxWidth={false}>
      <Box sx={{ mt: 2, mb: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleCancelBeforeCreate} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {businessName} — Update Proof
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Order Details */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Order Details</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Order ID:</strong> #{orderId}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Status:</strong> {updateOrder?.status || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Business:</strong> {businessName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Update Type:</strong>{' '}
                {updateOrder?.orderUpdateType || updateOrder?.updateType || 'N/A'}
              </Typography>
            </Grid>
            {updateOrder?.totalPrice != null && parseFloat(updateOrder.totalPrice) > 0 && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Cost:</strong> ${parseFloat(updateOrder.totalPrice).toFixed(2)}
                </Typography>
              </Grid>
            )}
            {relatedOrder && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Original Order:</strong> #{relatedOrder.id}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Original Order Ad Groups (for date extension context) */}
        {hasDates && relatedOrder?.audiences?.length > 0 && (
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
            <Typography variant="h6" gutterBottom>Original Order Ad Groups</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {relatedOrder.audiences.map((audience, idx) => (
                <Grid item xs={12} sm={6} md={4} key={audience.id || idx}>
                  <Card>
                    <CardContent sx={{ pb: 1 }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        {audience.schoolName || audience.organizationName || `Audience ${idx + 1}`}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Channel:</strong> {audience.channel}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Dates:</strong>{' '}
                        {audience.startDate && audience.endDate
                          ? `${formatDate(audience.startDate)} – ${formatDate(audience.endDate)}`
                          : 'N/A'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Update Details section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Update Details</Typography>
          <Divider sx={{ mb: 2 }} />

          {hasDates && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" color="primary" gutterBottom fontWeight={600}>
                End Date Extension
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">New End Date</Typography>
                  <Typography variant="body1" color="primary.main" fontWeight="bold">
                    {updateOrder?.newEndDate ? formatDate(updateOrder.newEndDate) : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Extension Cost</Typography>
                  <Typography variant="body1" color="success.main" fontWeight="bold">
                    ${parseFloat(updateOrder?.totalPrice || 0).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {hasLink && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" color="info.main" gutterBottom fontWeight={600}>
                Link Update
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Link Type</Typography>
                  <Typography variant="body1">{updateOrder?.eLinkType || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Link Content</Typography>
                  <Typography variant="body1">{updateOrder?.eLinkContent || 'N/A'}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {needsArtwork && (
            <Box>
              <Typography variant="subtitle1" color="warning.dark" gutterBottom fontWeight={600}>
                {hasHeader ? 'Header Update' : 'Artwork Update'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload the new {hasHeader ? 'header' : 'artwork'} image(s) for the client to review.
              </Typography>

              {/* Image upload area */}
              {artworkImages.length === 0 ? (
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="artwork-upload-input"
                    type="file"
                    multiple
                    onChange={handleImageUpload}
                  />
                  <label htmlFor="artwork-upload-input">
                    <Box
                      sx={{
                        border: '2px dashed',
                        borderColor: 'grey.300',
                        borderRadius: 1,
                        p: 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
                      }}
                    >
                      <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                      <Typography variant="body1" gutterBottom>
                        Click to upload artwork
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Supports: JPEG, PNG, GIF, WebP — multiple files allowed
                      </Typography>
                    </Box>
                  </label>
                </Box>
              ) : (
                <Box>
                  <Grid container spacing={2}>
                    {artworkImages.map((img) => (
                      <Grid item xs={12} sm={6} md={4} key={img.id}>
                        {img.compressing ? (
                          <Box
                            sx={{
                              display: 'flex', flexDirection: 'column', alignItems: 'center',
                              p: 3, border: '2px dashed', borderColor: 'primary.main', borderRadius: 1
                            }}
                          >
                            <CircularProgress sx={{ mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              Processing "{img.name}"…
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ position: 'relative' }}>
                            <img
                              src={img.preview}
                              alt={img.name}
                              style={{
                                width: '100%',
                                maxHeight: 220,
                                objectFit: 'contain',
                                border: '1px solid #ddd',
                                borderRadius: 4
                              }}
                            />
                            <IconButton
                              onClick={() => handleRemoveImage(img.id)}
                              size="small"
                              sx={{
                                position: 'absolute', top: 4, right: 4,
                                bgcolor: 'error.main', color: 'white',
                                '&:hover': { bgcolor: 'error.dark' }
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="caption" display="block" sx={{ mt: 0.5, px: 0.5 }} noWrap>
                              {img.name}
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                    ))}
                  </Grid>

                  {/* Add more images button */}
                  <Box sx={{ mt: 2 }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="artwork-upload-more"
                      type="file"
                      multiple
                      onChange={handleImageUpload}
                    />
                    <label htmlFor="artwork-upload-more">
                      <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} size="small">
                        Add More Images
                      </Button>
                    </label>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Paper>

        {/* Proof Notes */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Proof Notes{' '}
            <Chip label="Optional" size="small" variant="outlined" sx={{ ml: 1 }} />
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TextField
            fullWidth
            multiline
            rows={3}
            value={proofNotes}
            onChange={(e) => setProofNotes(e.target.value)}
            placeholder="Add any notes or instructions for the client…"
            variant="outlined"
          />
        </Paper>

        {/* Action Buttons */}
        {isCompressing && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Processing images… Please wait.
          </Alert>
        )}
        {needsArtwork && artworkImages.filter(i => i.file).length === 0 && !isCompressing && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Please upload at least one artwork image before creating the proof.
          </Alert>
        )}

        <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            onClick={handleCancelBeforeCreate}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            onClick={handleCreateProof}
            disabled={uploading || !canCreate || isCompressing}
          >
            {uploading ? 'Creating Proof…' : 'Create Proof'}
          </Button>
        </Box>

      </Box>
    </Container>
  );
};

export default UpdateProofCreate;
